import { JSX, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useUnsavedChangesCtx } from "../contexts/unsavedChanges";
import { useAuth } from "../contexts/auth";
import { Nav, Offcanvas } from "react-bootstrap";
import { IconDefinition, SizeProp } from "@fortawesome/fontawesome-svg-core";
import {
  faGauge,
  faUser,
  faUserTie,
  faListCheck,
  faFolder,
  faUserGroup,
  faVideo,
  faTimeline,
  faCircleNodes,
  faDumbbell,
  faCopy,
  faStopwatch,
  faLayerGroup,
  faCodeMerge,
  faPerson,
  faPersonWalking,
  faFolderOpen,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./AppSidebar.css";

export type NavigationLink = {
  key: string;
  icon: IconDefinition;
  title: string;
  url: string;
  hide?: boolean;
  level: number;
  group?: string;
};

const GROUP_LABELS: Record<string, string> = {
  gestione: "Gestione",
  libreria: "Libreria",
  parametri: "Parametri",
};

export const NAVIGATION_MENU: NavigationLink[] = [
  {
    key: "dashboard",
    icon: faGauge,
    title: "Dashboard",
    url: "/dashboard",
    level: 3,
  },
  {
    key: "programmazioni",
    icon: faFolder,
    title: "Programmazioni",
    url: "/programmazioni",
    level: 3,
  },
  {
    key: "public-schedules",
    icon: faFolderOpen,
    title: "Programmazioni Pubbliche",
    url: "/programmazioni-pubbliche",
    level: 3,
  },
  {
    key: "checks",
    icon: faListCheck,
    title: "Check Periodici",
    url: "/checks",
    level: 3,
  },
  {
    key: "tutorial",
    icon: faVideo,
    title: "Video Tutorial",
    url: "/video-tutorial",
    level: 3,
  },
  {
    key: "mentoring",
    icon: faUserGroup,
    title: "Mentoring",
    url: "/mentoring",
    level: 3,
  },
  {
    key: "area-personale",
    icon: faUser,
    title: "Area Personale",
    url: "/area-personale",
    level: 3,
  },
  {
    key: "utenti",
    icon: faUserGroup,
    title: "Utenti",
    url: "/utenti",
    level: 2,
    group: "gestione",
  },
  {
    key: "precompiled-progressions",
    icon: faChartLine,
    title: "Progressioni Precompilate",
    url: "/progressioni-precompilate",
    level: 2,
    group: "libreria",
  },
  {
    key: "exercises",
    icon: faDumbbell,
    title: "Esercizi",
    url: "/esercizi",
    level: 2,
    group: "libreria",
  },
  {
    key: "variants",
    icon: faCopy,
    title: "Varianti",
    url: "/varianti",
    level: 2,
    group: "libreria",
  },
  {
    key: "muscle-groups",
    icon: faCircleNodes,
    title: "Gruppi Muscolari",
    url: "/gruppi-muscolari",
    level: 2,
    group: "libreria",
  },
  {
    key: "motor-patterns",
    icon: faTimeline,
    title: "Pattern Motori",
    url: "/pattern-motori",
    level: 2,
    group: "libreria",
  },
  {
    key: "tecnics",
    icon: faUserTie,
    title: "Tecniche",
    url: "/tecniche",
    level: 2,
    group: "libreria",
  },
  {
    key: "tuts",
    icon: faUserTie,
    title: "Schemi TUT",
    url: "/time-under-tension",
    level: 2,
    group: "parametri",
  },
  {
    key: "recs",
    icon: faStopwatch,
    title: "Recupero",
    url: "/recupero",
    level: 2,
    group: "parametri",
  },
  {
    key: "rep-schemes",
    icon: faLayerGroup,
    title: "Rep Scheme",
    url: "/rep-scheme",
    level: 2,
    group: "parametri",
  },
  {
    key: "rep-scheme-types",
    icon: faCodeMerge,
    title: "Rep Scheme Type",
    url: "/rep-scheme-type",
    level: 2,
    group: "parametri",
  },
  {
    key: "efforts",
    icon: faPerson,
    title: "Effort",
    url: "/effort",
    level: 2,
    group: "parametri",
  },
  {
    key: "effort-types",
    icon: faPersonWalking,
    title: "Effort Type",
    url: "/effort-type",
    level: 2,
    group: "parametri",
  },
];

type Props = {
  show: boolean;
  handleClose: VoidFunction;
};

type LinkProps = {
  link: NavigationLink;
  vertical?: boolean;
  iconSize?: SizeProp;
};

export function SidebarLink({ link, iconSize, vertical }: LinkProps) {
  const location = useLocation();
  const { safeNavigate } = useUnsavedChangesCtx();
  const match = location.pathname.startsWith(link.url);

  return (
    <span
      className={classNames("sidebar-link", { active: match })}
      onClick={() => safeNavigate(link.url)}
      style={{ cursor: "pointer" }}
    >
      <FontAwesomeIcon icon={link.icon} fixedWidth={true} size={iconSize} />
      <span>{link.title}</span>
    </span>
  );
}

export default function AppSidebar({ show, handleClose }: Props) {
  const location = useLocation();
  const { loggedUser } = useAuth();

  useEffect(
    () => handleClose(),
    // eslint-disable-next-line
    [location.pathname]
  );

  const visibleLinks = NAVIGATION_MENU.filter((link) => !link.hide).filter(
    (link) => +loggedUser?.role! <= link.level
  );

  const renderedItems: JSX.Element[] = [];
  let lastGroup: string | undefined = undefined;

  visibleLinks.forEach((link) => {
    const currentGroup = link.group;
    if (currentGroup !== lastGroup && currentGroup && GROUP_LABELS[currentGroup]) {
      renderedItems.push(
        <div key={`group-${currentGroup}`} className="sidebar-group-label">
          {GROUP_LABELS[currentGroup]}
        </div>
      );
    }
    lastGroup = currentGroup;
    renderedItems.push(<SidebarLink key={link.key} link={link} />);
  });

  const userInitials = `${loggedUser?.nome?.[0] ?? ""}${loggedUser?.cognome?.[0] ?? ""}`.toUpperCase();

  return (
    <Offcanvas show={show} onHide={handleClose} className="sidebar-offcanvas">
      <div className="get-move-sidebar d-flex flex-column h-100">
        <Offcanvas.Header className="sidebar-header" closeButton>
          <Offcanvas.Title className="sidebar-brand">
            <span className="sidebar-brand-mark">
              <img
                src={require("../assets/img/LOGO CLAUDIO MESSINA_MONOGRAMMA PNG_MONOGRAMMA BIANCO-05.png")}
                alt=""
              />
            </span>
            <span className="sidebar-brand-word">Coaching Master</span>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <div className="sidebar-userstrip">
          <div className="sidebar-avatar">{userInitials}</div>
          <div className="sidebar-user-text">
            <div className="sidebar-user-name">{`${loggedUser?.nome ?? ""} ${loggedUser?.cognome ?? ""}`}</div>
          </div>
        </div>

        <Offcanvas.Body className="sidebar-content flex-grow-1 d-flex flex-column px-3 py-3">
          <Nav className="flex-column flex-grow-1 gap-1">
            {renderedItems}
          </Nav>
        </Offcanvas.Body>
      </div>
    </Offcanvas>
  );
}
